/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import styles from './styles.module.scss';
import cx from 'classnames';
import root from 'window-or-global';
import { useEffect, useMemo, useState } from 'react';
import { useLabels } from '@/hooks/useLabels';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useGetProductSkuPrice } from '@/features/product/api/getProductSkuPrice';
import { useLocale } from '@/hooks/useLocale';
import { useHeaderData } from '@/features/header/api/getHeader';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useFeatureFlag } from '@/features/globalConfig';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getAvailableCertonaProducts } from '@/utils/getAvailableCertonaProducts';
import { CMSYmme } from '@/features/contentstack/components/CMSYmme/CMSYmme';
import { SmartLink } from '@/utils/smartLink';
import { DoesNotFitNotification } from './DoesNotFitNotification';
import { View, Text, Skeleton } from '@az/starc-ui';
import {
  CertonaBlock,
  HorizontalScrolledPartCardSkeleton,
  type CertonaRecommendation,
  type AddCertonaItemObjectType,
} from '@/features/certona';
import { countryCodes } from '@/constants/locale';
import { routePaths } from '@/constants/routePaths';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { pageTypes } from '@/constants/page';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';

type CertonaContentForOos = {
  '@type': string;
  containerID: string;
  desktop: boolean;
  mobile: boolean;
  name: string;
  tablet: boolean;
  themeCode: string;
};

type NewCertonaForOosProps = {
  isOos: boolean;
  doesNotFit: boolean;
  logAnalytics: () => void;
  certonaRecommendations?: CertonaRecommendation[];
  setCertonaRecommendationAddedToCart: React.Dispatch<React.SetStateAction<boolean>>;
  setAddedCertonaItemObject: React.Dispatch<React.SetStateAction<AddCertonaItemObjectType | null>>;
};

const certonaContentForOos: CertonaContentForOos = {
  '@type': 'PDPPageBody_ProductDetail',
  containerID: 'pencilproduct1_rr',
  desktop: true,
  mobile: true,
  name: 'Certona Content Block',
  tablet: true,
  themeCode: 'grey',
};

const labelsMap = {
  ADD_YOUR_VEHCILE: 'label_First_add_your_vehicle',
  GET_AN_EXACT_FIT_FOR_YOUR_VEHICLE: 'hyperlink_YMME_Body_GetAnExactFitForYourVehicle',
  appPdpOosThisPartIsOutOfStock: 'label_ProductDetailsPage_OOS_This_Product_Is_Out_Of_Stock',
  appPdpOosShopSimilarItemsBelow: 'label_ProductDetailsPage_OOS_Shop_similar_items_below',
  appPdpDoesNotFitThesePartsFit: 'label_ProductDetailsPage_Does_Not_Fit_These_Parts_Fit',
  appPdpDoesNotFitYour: 'label_MyAccountMyProfile_landing_RewardYour',
  labelShopAll: 'label_Homepage_content_ShopAll',
};

type ComponentDisplayOption = 'skeleton' | 'container' | 'warning' | 'ymme';

export const NewCertonaForOos = ({
  isOos,
  doesNotFit,
  logAnalytics,
  certonaRecommendations,
  setCertonaRecommendationAddedToCart,
  setAddedCertonaItemObject,
}: NewCertonaForOosProps) => {
  const { acesEnabled } = useAcesFlags();
  const labels = useLabels(labelsMap);

  const isCertonaEnabledForOosOrDoesNotFit =
    useFeatureFlag('IS_OOS_OR_DOES_NOT_FIT_CONTAINER_ENABLED') === 'true';
  const isPdpCertonaAddToCartEnabled = useCertonaAddToCartFlag();

  const [availableProductsForPencilLength, setAvailableProductsForPencilLength] = useState(0);

  const { data: headerData } = useHeaderData();
  const hasVehicle = headerData?.vehicleMap?.vehicleId !== '';
  const storeNumber = headerData?.storeNumber;

  const { data: productDetailsData } = useProductDetails();
  const { vehicleFitFlag, breadCrumbs, recordType, vehicleSpecificFlag } =
    productDetailsData?.product ?? {};
  const shelfSeoUrl =
    breadCrumbs?.find(
      (item) => item.pageType === 'ProductShelf' || (acesEnabled && item.pageType === 'PartGroup'),
    )?.seoUrl || routePaths.blankURL;

  const locale = useLocale();
  const preferredVehicle = usePreferredVehicle();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.only('lg'));

  const [displayOption, setDisplayOption] = useState<ComponentDisplayOption>('skeleton');

  const vehicleTextForDoesNotFit = `${preferredVehicle?.year} ${preferredVehicle?.make} ${preferredVehicle?.model}.`;

  const index = certonaRecommendations?.findIndex((item) =>
    item.scheme.includes(certonaContentForOos.containerID || ''),
  );
  const items = certonaRecommendations?.[index ?? -1]?.items;
  const skuNumbers = items?.map((part) => part.id);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setHasTimedOut(true);
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, []);
  const shownItemsAmount = isPdpCertonaAddToCartEnabled
    ? availableProductsForPencilLength
    : items?.length;

  const { data: pricingInfo, isSuccess: pricingIsSuccess } = useGetProductSkuPrice({
    skuNumbers,
    storeNumber,
    enabled: !!storeNumber && !!skuNumbers?.length,
    staleTime: 5 * 60000, // 5 minutes
  });
  const hasPricingData =
    pricingIsSuccess ||
    skuNumbers?.length === 0 ||
    (certonaRecommendations !== undefined && !items?.length);

  const updateAfterAddtoCartClick = () => {
    setCertonaRecommendationAddedToCart(true);
  };
  const availableProductsForPencil = useMemo(
    () =>
      getAvailableCertonaProducts({
        items,
        skuDetailsData: null,
        pricingInfo,
        filterByPriceOnly: true,
      }) ?? [],
    [items, pricingInfo],
  );

  useEffect(() => {
    setAvailableProductsForPencilLength(availableProductsForPencil.length);

    const shouldShowFitmentMessage = locale === countryCodes.enUS && hasVehicle && doesNotFit;

    if (
      certonaRecommendations?.length &&
      availableProductsForPencil.length &&
      hasPricingData &&
      root.certonaNotCalled === false &&
      root.certonaNotCalled !== undefined
    ) {
      setDisplayOption('container');
    } else if (
      hasPricingData &&
      root.certonaNotCalled === false &&
      root.certonaNotCalled !== undefined &&
      !availableProductsForPencil.length
    ) {
      if (
        isOos &&
        ((!hasVehicle &&
          ((!vehicleFitFlag && recordType !== 'app') || (acesEnabled && !vehicleSpecificFlag))) ||
          (hasVehicle &&
            (!vehicleFitFlag ||
              (vehicleFitFlag && recordType === 'app') ||
              (acesEnabled && !vehicleSpecificFlag))))
      ) {
        setDisplayOption('ymme');
      } else if (shouldShowFitmentMessage) {
        setDisplayOption('warning');
      }
    } else if (!availableProductsForPencil.length && shouldShowFitmentMessage && hasPricingData) {
      setDisplayOption('warning');
    } else if (hasTimedOut) {
      if (shouldShowFitmentMessage) {
        setDisplayOption('warning');
      } else if (
        isOos &&
        ((!hasVehicle &&
          ((!vehicleFitFlag && recordType !== 'app') || (acesEnabled && !vehicleSpecificFlag))) ||
          (hasVehicle &&
            (!vehicleFitFlag ||
              (vehicleFitFlag && recordType === 'app') ||
              (acesEnabled && !vehicleSpecificFlag))))
      ) {
        setDisplayOption('ymme');
      } else {
        setDisplayOption('warning');
      }
    }
  }, [
    availableProductsForPencil,
    items,
    pricingInfo,
    hasPricingData,
    certonaRecommendations?.length,
    doesNotFit,
    hasVehicle,
    isOos,
    recordType,
    locale,
    vehicleFitFlag,
    hasTimedOut,
    acesEnabled,
    vehicleSpecificFlag,
  ]);

  const truncateText = (text: string, maxCharacters?: number) => {
    if (maxCharacters === undefined) {
      return text;
    } else if (maxCharacters < 1) {
      return;
    } else {
      let truncatedText = text.slice(0, maxCharacters - 1);
      if (text.length > maxCharacters) {
        truncatedText = truncatedText.trimEnd() + '\u2026';
      }
      return truncatedText;
    }
  };

  const title = availableProductsForPencilLength ? (
    <View
      direction="row"
      justify={{
        s: 'space-between',
        m: availableProductsForPencilLength >= 2 ? 'space-between' : 'start',
        l: availableProductsForPencilLength >= 2 ? 'space-between' : 'start',
      }}
      align={isMobile ? 'start' : 'center'}
      padding={{ s: 0, m: [3, 0, 0, 14 / 4], l: [3, 0, 0, 14 / 4] }}
      className={styles.titlesContainer}
    >
      <View.Item>
        {isOos ? (
          <Text className={styles.pdpOosText} attributes={{ 'data-testid': 'oos_title_text' }}>
            <span className={styles.pdpOosBoldText}>
              {`${labels.appPdpOosThisPartIsOutOfStock}`}.
            </span>{' '}
            <span
              className={availableProductsForPencilLength === 1 ? styles.shopAllMobileSpace : ''}
            >
              {`${labels.appPdpOosShopSimilarItemsBelow}`}.
            </span>
          </Text>
        ) : (
          doesNotFit && (
            <Text className={styles.pdpOosText} attributes={{ 'data-testid': 'dnf_title_text' }}>
              <span
                className={styles.pdpOosBoldText}
              >{`${labels.appPdpDoesNotFitThesePartsFit} ${labels.appPdpDoesNotFitYour} `}</span>{' '}
              <span>
                {truncateText(
                  vehicleTextForDoesNotFit,
                  isMobile
                    ? 31
                    : isTablet
                      ? 45
                      : isDesktop && shownItemsAmount === 2
                        ? 46
                        : undefined,
                )}
              </span>
            </Text>
          )
        )}
      </View.Item>
      <View.Item>
        <SmartLink to={shelfSeoUrl!} onClick={logAnalytics}>
          <Text
            decoration="underline"
            className={cx(styles.shopAllStyles, {
              [styles.threeOrMoreItemsAvailable]: shownItemsAmount && shownItemsAmount >= 3,
              [styles.twoItemsAvailable]: shownItemsAmount && shownItemsAmount === 2,
            })}
          >
            {labels.labelShopAll}
          </Text>
        </SmartLink>
      </View.Item>
    </View>
  ) : (
    ''
  );

  if (displayOption === 'ymme') {
    return (
      <View padding={[0, 0, 5, 0]} attributes={{ 'data-testid': 'ymme_certona_container' }}>
        <View
          className={cx({
            [styles.sharedOOSWrapper]: isCertonaEnabledForOosOrDoesNotFit && (isOos || doesNotFit),
          })}
          justify={
            isCertonaEnabledForOosOrDoesNotFit && (isOos || doesNotFit) ? 'center' : undefined
          }
        >
          <CMSYmme pageType={pageTypes.ProductDetail} removeMargins />
        </View>
      </View>
    );
  }

  if (displayOption === 'warning') {
    return <DoesNotFitNotification href={shelfSeoUrl} />;
  }

  if (displayOption === 'container') {
    return (
      <View attributes={{ 'data-testid': 'certona_recommendations_container' }}>
        <CertonaBlock
          className={
            availableProductsForPencilLength
              ? isOos || doesNotFit
                ? styles.oosAndDoesNotFitCertonaContainer
                : ''
              : ''
          }
          title={title}
          contentItem={certonaContentForOos}
          certonaRecommendations={certonaRecommendations}
          onCertonaAddToCartClick={() => {
            updateAfterAddtoCartClick();
          }}
          setAddedCertonaItemObject={setAddedCertonaItemObject}
          containerType="horizontal_scrolled"
          certonaType={isOos ? 'OOS' : doesNotFit ? 'DNF' : undefined}
          shelfSeoUrl={shelfSeoUrl}
          updateAfterAddtoCartClick={updateAfterAddtoCartClick}
          inDrawer={false}
          onlyShowAvailableProductRecommendations={isPdpCertonaAddToCartEnabled}
        />
      </View>
    );
  }

  return <SkeletonWithTitle />;
};

const SkeletonWithTitle = () => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  return (
    <View
      direction="column"
      className={styles.horizontalSkeletonWrapper}
      attributes={{ 'data-testid': 'certona-container-skeleton' }}
    >
      <View.Item>
        <View direction="row" justify="space-between">
          <View.Item>
            <Skeleton width={{ m: '403px', s: '215px' }} height={{ m: '28px', s: '50px' }} />
          </View.Item>
          <View.Item
            gapBefore={isMobile ? 12 : 0}
            attributes={{ style: { paddingRight: isMobile ? '16px' : '20px' } }}
          >
            <Skeleton width="80px" height="28px" />
          </View.Item>
        </View>
      </View.Item>
      <HorizontalScrolledPartCardSkeleton className={styles.horizontalSkeleton} />
    </View>
  );
};
